import { useMemo } from 'react';
import { useHasScope } from '../../userInfo/hooks/useHasScope';

/**
 * Hook that checks if the current user can ONLY create private views
 * (i.e., they cannot create public or team views).
 *
 * @param scope - The scope required to create public/team views
 * @returns {data, loading, error} where data is true if the user does NOT have the scope
 *          (meaning they can only create private views)
 */
export const useCanCreatePrivateViewsOnly = scope => {
  const {
    data: hasScope,
    loading,
    error
  } = useHasScope(scope || '');
  return useMemo(() => {
    // If no scope is provided, user is not restricted (can create public views)
    if (!scope) {
      return {
        data: false,
        loading: false,
        error: undefined
      };
    }

    // If still loading, data should be undefined
    if (loading) {
      return {
        data: undefined,
        loading,
        error
      };
    }

    // Return true if user does NOT have the scope (restricted to private views only)
    return {
      data: !hasScope,
      loading,
      error
    };
  }, [scope, hasScope, loading, error]);
};